import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareEvents = (e1, e2) => e1.id === e2.id;

const eventSlice = createSlice({
    name: 'events',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload
        },
        addOrUpdate: (state, action) => {
            for (const e of ensureArray(action.payload)) {
                let idx = state.data.findIndex(e2 => compareEvents(e, e2));
                if (idx >= 0) {
                    state.data.splice(idx, 1, e);
                } else {
                    state.data.push(e);
                }
            }
        },
        remove: (state, action) => {
            for (const event of ensureArray(action.payload)) {
                let idx = state.data.findIndex(e => compareEvents(e, event));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        },
        addActivity: (state, action) => {
            for (const activity of ensureArray(action.payload)) {
                let idx = state.data.findIndex(e => e.id === activity.eventid);
                if (idx >= 0) {
                    let event = {...state.data[idx]};
                    if (!event.active) {
                        event.active = [activity];
                    } else if (!event.active.find(a => a.id === activity.id)) {
                        event.active.push(activity);
                    }
                    state.data.splice(idx, 1, event);
                }
            }
        },
        removeActivity: (state, action) => {
            for (const activity of ensureArray(action.payload)) {
                let event = state.data.find(e => e.id === activity.eventid);
                if (event?.active) {
                    let idx = event.active.findIndex(e => e.id === activity.id);
                    if (idx >= 0) {
                        event.active.splice(idx, 1);
                    }
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => eventSlice.getInitialState());
    }
});

// #region network operations
const fetchEvents = network => network.get('/api/v1/events', eventSlice.actions.set);
export const createEvent = (network, event) => network.put('/api/v1/event', event, eventSlice.actions.addOrUpdate);
export const updateEvent = (network, delta) => network.post('/api/v1/event', delta, eventSlice.actions.addOrUpdate);
export const removeEvent = (network, id) => network.delete('/api/v1/event/' + encodeURIComponent(id), eventSlice.actions.remove);
export const addEventActiveTime = (network, activity) => network.put('/api/v1/eventactivity', activity, eventSlice.actions.addActivity);
export const removeEventActiveTime = (network, id) => network.delete('/api/v1/eventactivity/' + encodeURIComponent(id), eventSlice.actions.removeActivity);
// #endregion

// #region permission checks
export const canUpdateEvent = (credentials, event) => credentials.access.hasFull('administrator');
export const canCreateEvent = (credentials, event) => credentials.access.hasFull('administrator');
export const canRemoveEvent = (credentials, event) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectEvents = state => state.events.data;
const selectEvent = id => createSelector([selectEvents], events => events.find(e => e.id === id));

// we now make a reselect to memoize a select function parameterized by jobid and expose that factory through a convenience function
const selectEventBySiteLevelAndType = createSelector([selectEvents, (events, site) => site, (events, site, level) => level, (events, site, level, type) => type], (events, site, level, type) => {
    return events.filter(p => (site === null || p.site === null || p.site === site) && (level === null || p.level === null || p.level === level) && (type === null || p.type === null || p.type === type))
});
const selectEventForSiteLevelType = (site, level, type) => state => selectEventBySiteLevelAndType(state, site, level, type);
// #endregion

// #region listeners
const listen = {
    eventCreated: (event, { dispatch }) => dispatch(eventSlice.actions.addOrUpdate(event)),
    eventUpdated: (event, { dispatch }) => dispatch(eventSlice.actions.addOrUpdate(event)),
    eventRemoved: (event, { dispatch }) => dispatch(eventSlice.actions.remove(event)),
    eventActivityCreated: (activity, { dispatch }) => dispatch(eventSlice.actions.addActivity(activity)),
    eventActivityRemoved: (activity, { dispatch }) => dispatch(eventSlice.actions.removeActivity(activity))
}
// #endregion
const init = { run: fetchEvents, listen };
export const [useEvent, realizeEvent] = dataHookFactory(selectEvent, id => init);
export const [useEvents] = dataHookFactory(selectEvents, init);
export const [useEventsForSiteLevelType] = dataHookFactory(selectEventForSiteLevelType, (site, level, type) => ({ run: fetchEvents, listen }));

const reducerDetails = { name: eventSlice.name, reducer: eventSlice.reducer };
export default reducerDetails;